/**
 * @fileoverview Own the document-edit AI access editor and its saved-versus-draft state. The backend supplies credential readiness, metrics, and accepted pilot identities; explicit patch requests change access settings. `AiControlCenter.js` owns feature/model navigation. UI choices remain subject to backend document permissions and execution admission; the employee list supplies identities rather than proof of access.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../api'
import { AppButton, AppIcon, AppToggle } from '../shared'
import { AdminCard, AdminNote, AdminSection } from './AdminChrome'
import './DocumentAiSettings.css'

const DEFAULTS = {
    enabled: false,
    rollout: 'pilot',
    pilot_identities: [],
    reasoning_effort: 'none',
}



function normalizedSettings(payload = {}) {
    return {
        ...DEFAULTS,
        enabled: Boolean(payload.enabled),
        rollout: 'pilot',
        pilot_identities: [...new Set((payload.pilot_identities || [])
            .map((identity) => String(identity || '').trim().toLowerCase())
            .filter(Boolean))],
        reasoning_effort: payload.reasoning_effort === 'low' ? 'low' : 'none',
        credential_ready: Boolean(payload.credential_ready),
        metrics: payload.metrics || {},
    }
}



function employeeIdentity(employee) {
    return String(employee?.Email || '').trim().toLowerCase()
}



function DocumentAiSettings({ embedded = false, accessOnly = false }) {
    const [saved, setSaved] = useState(null)
    const [form, setForm] = useState(DEFAULTS)
    const [employees, setEmployees] = useState([])
    const [accessSearch, setAccessSearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')

    const load = useCallback(

    async () => {
        setLoading(true)
        setError('')
        try {
            const [settingsResponse, employeesResponse] = await Promise.all([
                api.get('/admin/document-ai/settings'),
                api.get('/employees'),
            ])
            const next = normalizedSettings(settingsResponse.data?.settings)
            setSaved(next)
            setForm(next)
            setEmployees((Array.isArray(employeesResponse.data) ? employeesResponse.data : [])
                .filter((employee) => employee?.IsActive !== false && employeeIdentity(employee))
                .sort((left, right) => String(left?.Name || left?.Email || '')
                    .localeCompare(String(right?.Name || right?.Email || ''))))
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Document AI settings failed to load.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load() }, [load])

    const dirty = useMemo(() => saved && JSON.stringify(form) !== JSON.stringify(saved), [form, saved])
    const update =

    (key, value) => {
        setNotice('')
        setForm((current) => ({ ...current, [key]: value }))
    }
    const save =

    async () => {
        setSaving(true)
        setError('')
        setNotice('')
        try {
            const response = await api.patch('/admin/document-ai/settings', {
                enabled: form.enabled,
                pilot_identities: form.pilot_identities,
            })
            const next = normalizedSettings(response.data?.settings)
            setSaved(next)
            setForm(next)
            setNotice(next.enabled ? 'Document AI access saved.' : 'Document AI is disabled for new edits.')
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Document AI settings could not be saved.')
        } finally {
            setSaving(false)
        }
    }

    const metrics = form.metrics || {}
    const selectedIdentities = new Set(form.pilot_identities || [])
    const acceptingRequests = Boolean(form.enabled && selectedIdentities.size)
    const employeeIdentities = new Set(employees.map(employeeIdentity))
    const unmatchedIdentities = [...selectedIdentities]
        .filter((identity) => !employeeIdentities.has(identity))
        .filter((identity) => !accessSearch.trim() || identity.includes(accessSearch.trim().toLowerCase()))
    const visibleEmployees = employees.filter((employee) => {
        const query = accessSearch.trim().toLowerCase()
        if (!query) return true
        return `${employee?.Name || ''} ${employee?.Email || ''}`.toLowerCase().includes(query)
    })
    const toggleEmployee =

    (employee) => {
        const identity = employeeIdentity(employee)
        if (!identity) return
        const next = selectedIdentities.has(identity)
            ? form.pilot_identities.filter((item) => item !== identity)
            : [...form.pilot_identities, identity]
        update('pilot_identities', next.sort())
    }
    const Wrapper = embedded ? AdminSection : 'div'
    const wrapperProps = embedded ? {
        title: 'Document AI',
        description: 'Choose who can request AI edits.',
        actions: (
            <AppButton
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
                label={saving ? 'Saving...' : dirty ? 'Save changes' : 'Saved'}
                onClick={save}
                disabled={saving || !dirty}
            />
        ),
    } : {}

    return (
        <div className={accessOnly ? 'document-ai-admin document-ai-admin--access-only' : 'document-ai-admin'}>
            <Wrapper {...wrapperProps}>
                {!embedded && <AppButton label={saving ? 'Saving…' : 'Save access'} onClick={save} disabled={saving || !dirty} />}
                {loading ? <AdminNote>Loading Document AI settings...</AdminNote> : null}
                {error ? <AdminNote tone="warning" title="Document AI unavailable">{error}</AdminNote> : null}
                {notice ? <AdminNote tone="success">{notice}</AdminNote> : null}
                {!loading && saved ? (
                    <>
                        <AdminCard>
                            <div className="document-ai-admin__launch">
                                <span className={`document-ai-admin__status ${acceptingRequests ? 'is-on' : 'is-off'}`} aria-hidden="true">
                                    <AppIcon icon={acceptingRequests ? 'pi pi-sparkles' : 'pi pi-pause'} />
                                </span>
                                <div>
                                    <strong>AI document edits</strong>
                                    <span>{form.enabled
                                        ? selectedIdentities.size
                                            ? `${selectedIdentities.size} ${selectedIdentities.size === 1 ? 'person can' : 'people can'} request edits`
                                            : 'No one currently has access'
                                        : 'No new requests can start'}</span>
                                </div>
                                {dirty ? <em>Unsaved</em> : null}
                                <AppToggle
                                    id="document-ai-enabled"
                                    checked={form.enabled}
                                    onChange={(event) => update('enabled', event.value)}
                                    ariaLabel="Enable AI document edits"
                                />
                            </div>
                            <div className="document-ai-admin__readiness">
                                <span className={form.credential_ready ? 'is-ready' : 'is-missing'}>
                                    <AppIcon icon={form.credential_ready ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} />
                                    OpenAI key {form.credential_ready ? 'ready' : 'missing'}
                                </span>
                                <span><AppIcon icon="pi pi-shield" /> Proposals never become current without review</span>
                                <span><AppIcon icon="pi pi-database" /> OpenAI response storage off</span>
                            </div>
                        </AdminCard>

                        <div className="document-ai-admin__grid">
                            <AdminCard title="Access" description="Only selected people see AI editing when they open a document.">
                                <div className="document-ai-admin__access-summary">
                                    <span><AppIcon icon="pi pi-users" aria-hidden="true" /> {selectedIdentities.size} selected</span>
                                    <label>
                                        <AppIcon icon="pi pi-search" aria-hidden="true" />
                                        <input
                                            type="search"
                                            value={accessSearch}
                                            onChange={(event) => setAccessSearch(event.target.value)}
                                            aria-label="Search people for Document AI access"
                                            placeholder="Search people" />
                                    </label>
                                </div>
                                <div className="document-ai-admin__access-list" role="group" aria-label="People with Document AI access">
                                    {visibleEmployees.map((employee) => {
                                        const identity = employeeIdentity(employee)
                                        const selected = selectedIdentities.has(identity)
                                        return (
                                            <button
                                                type="button"
                                                key={employee.id || identity}
                                                className={selected ? 'is-selected' : ''}
                                                role="checkbox"
                                                aria-checked={selected}
                                                onClick={() => toggleEmployee(employee)}>
                                                <span className="document-ai-admin__person-avatar" aria-hidden="true">
                                                    {String(employee?.Name || employee?.Email || '?').trim().charAt(0).toUpperCase()}
                                                </span>
                                                <span>
                                                    <strong>{employee?.Name || identity}</strong>
                                                    <small>{identity}</small>
                                                </span>
                                                <AppIcon icon={selected ? 'pi pi-check-circle' : 'pi pi-circle'} aria-hidden="true" />
                                            </button>
                                        )
                                    })}
                                    {unmatchedIdentities.map((identity) => (
                                        <button
                                            type="button"
                                            key={identity}
                                            className="is-selected"
                                            role="checkbox"
                                            aria-checked="true"
                                            onClick={() => update(
                                                'pilot_identities',
                                                form.pilot_identities.filter((item) => item !== identity),
                                            )}>
                                            <span className="document-ai-admin__person-avatar" aria-hidden="true">?</span>
                                            <span>
                                                <strong>Stored account identity</strong>
                                                <small>{identity}</small>
                                            </span>
                                            <AppIcon icon="pi pi-times-circle" aria-hidden="true" />
                                        </button>
                                    ))}
                                    {!visibleEmployees.length && !unmatchedIdentities.length ? (
                                        <div className="document-ai-admin__access-empty">
                                            <AppIcon icon="pi pi-user-minus" aria-hidden="true" />
                                            {employees.length ? 'No people match this search.' : 'No active employees with email addresses were found.'}
                                        </div>
                                    ) : null}
                                </div>
                            </AdminCard>

                            {!accessOnly && <AdminCard title="Model settings"><a href="/admin?tab=ai&view=features&feature=document-ai-edits">Manage models in AI control center →</a></AdminCard>}
                        </div>

                        <AdminCard title="Last 30 days" description="Metadata only; document text and instructions are excluded from telemetry.">
                            <div className="document-ai-admin__metrics">
                                <Metric label="Requests" value={metrics.total_runs || 0} />
                                <Metric label="Adopted" value={`${metrics.adoption_rate || 0}%`} />
                                <Metric label="Proposal p50" value={formatDuration(metrics.proposal_p50_ms)} />
                                <Metric label="Proposal p95" value={formatDuration(metrics.proposal_p95_ms)} />
                                <Metric label="Input tokens" value={formatNumber(metrics.input_tokens)} />
                                <Metric label="Cached tokens" value={formatNumber(metrics.cached_input_tokens)} />
                            </div>
                        </AdminCard>
                    </>
                ) : null}
            </Wrapper>
        </div>
    )
}



function Metric({ label, value }) {
    return <div><span>{label}</span><strong>{value}</strong></div>
}



function formatDuration(milliseconds) {
    if (!milliseconds) return '—'
    return milliseconds < 1000 ? `${milliseconds} ms` : `${(milliseconds / 1000).toFixed(1)} s`
}



function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(Number(value || 0))
}

export default DocumentAiSettings
